/**
 * @ngdoc services
 * @memberof mercury.modules.stock
 * @name StockPurchaseOrders
 * @description Data access service for managing stock purchase orders
 */
angular.module('mercury.modules.stock.purchaseorder')
.factory('StockPurchaseOrders', ["$filter", "$q", "$resource", "$window", "settings", "helperUtils", "logger", function($filter, $q, $resource, $window, settings, helperUtils, logger) {
  var PurchaseOrders = $resource(settings.apiHost + '/r/StockPurchaseOrders/:id', {id: '@id', purchaseOrderId: '@purchaseOrderId'}, {
    update: {method: 'PUT'},
    patch: {method: 'PATCH'},
    print: {url: settings.apiHost + '/a/StockPurchaseOrders/GeneratePdf', method: 'GET'},
    email: {url: settings.apiHost + '/a/StockPurchaseOrders/SendEmail/:purchaseOrderId', method: 'POST'},
    fax: {url: settings.apiHost + '/a/StockPurchaseOrders/SendFax/:purchaseOrderId', method: 'POST'},
    direct: {url: settings.apiHost + '/a/StockPurchaseOrders/SendDirect/:id', method: 'POST'}
  });
  var StockOrders = $resource(settings.apiHost + '/r/StockOrders/:id', {id: '@id'});
  var StockOrderCommunicationLogs = $resource(settings.apiHost + '/r/StockPurchaseOrderCommunicationLogs/:id', {id: '@id'});
  var StockPurchaseOrderLines = $resource(settings.apiHost + '/r/StockPurchaseOrderLines/:id', {id: '@id'});
  var purchaseOrderPollDelaysInMs = helperUtils.generateExponentialDelays(5, 320)
    .map(function(delaySeconds) { return delaySeconds * 1000; });

  // Below are new endpoints to the StockAPI for this call (Thuis will only be here while we still use the angular page)
  var ReceivedPurchaseOrdersByDateRange = $resource(`${settings.stockHost}/internal/purchase-orders`)
  var PurchaseOrderStockApi = $resource(`${settings.stockHost}/internal/purchase-orders/:id`, {id: '@id'})

  /**
   * Get a specific purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {String} id Id of the purchase order to get
   */
  function get(id) {
    return PurchaseOrders.get({
      id: id
    });
  }

  /**
   * Get a specific purchase order via the Stock API
   *
   * @memberof StockPurchaseOrders
   * @param  {String} id Id of the purchase order to get
   */
    function getStockApi(id) {
      return PurchaseOrderStockApi.get({
        id: id
      });
    }

  /**
   * Get received purchase orders between a given period
   *
   * @memberof StockPurchaseOrders
   * @param  {String} startTime start of period
   * @param  {String} endTime   end of period
   */
  function getArchive(startTime, endTime) {
    return PurchaseOrders.query({
      $filter: 'DatestampInvoiced ge ' + startTime +
        ' and DatestampInvoiced lt ' + endTime +
        ' and DatestampDeleted eq null and Received eq true',
      $orderby: 'DatestampInvoiced'
    });
  }

  function getReceivedContaining(startTime, endTime, stockItemId) {
    return PurchaseOrders.query({
      startTime: startTime,
      endTime: endTime,
      stockItemId: stockItemId
    });
  }

  /**
   * Get received purchase order lines between a given period and for a given
   * reporting condition (purchase order created, received, invoiced date).
   *
   * @memberof StockPurchaseOrders
   * @param  {String} startTime start of period
   * @param  {String} endTime   end of period
   * @param  {String} purchaseOrderCondition reporting condition type
   */
  function getLineArchive(startTime, endTime, purchaseOrderCondition) {
    return StockPurchaseOrderLines.query({
      $filter: purchaseOrderCondition + ' ge ' + startTime +
        ' and ' + purchaseOrderCondition + ' lt ' + endTime +
        ' and PurchaseOrderDatestampDeleted eq null and ' + purchaseOrderCondition + ' ne null',
      $orderby: purchaseOrderCondition
    });
  }

  /**
   * Get received purchase orders between a given period and for a given
   * reporting property (purchase order created, received, invoiced date).
   *
   * @memberof StockPurchaseOrders
   * @param  {String} from start of period
   * @param  {String} to   end of period
   * @param  {String} property reporting property
   */
  function getReceivedPurchaseOrdersByDateRange(from, to, property) {
    return ReceivedPurchaseOrdersByDateRange.query({
      from,
      to,
      property
    });
  }

  /**
   * Get received purchase order lines between a given period for a specific stock item
   *
   * @memberof StockPurchaseOrders
   * @param  {String} startTime start of period
   * @param  {String} endTime   end of period
   * @param  {String} itemId    Id of stock item
   */
  function getLineItemArchive(startTime, endTime, itemId) {
    return StockPurchaseOrderLines.query({
      $filter: 'PurchaseOrderDatestampReceived ge ' + startTime +
        ' and PurchaseOrderDatestampReceived lt ' + endTime +
        ' and StockItemId eq ' + itemId +
        ' and PurchaseOrderDatestampDeleted eq null and PurchaseOrderDatestampReceived ne null',
      $orderby: 'PurchaseOrderDatestampReceived'
    });
  }

  /**
   * Get all the purchase orders for a given statement
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} statement Statement to get the orders for
   */
  function getStatementOrders(statement) {
    var promise = $q.defer();

    var statementOrders = PurchaseOrders.query({
      $filter: 'DatestampInvoiced ge ' + statement.datestampStart +
        ' and DatestampInvoiced lt ' + statement.datestampEnd +
        ' and SupplierId eq ' + statement.supplierId +
        ' and DatestampDeleted eq null and Received eq true',
      $orderby: 'DatestampInvoiced'
    });

    var ordersBefore = PurchaseOrders.query({
      $filter: 'DatestampInvoiced lt ' + statement.datestampStart +
        ' and SupplierId eq ' + statement.supplierId +
        ' and DatestampDeleted eq null and Received eq true',
      $top: 2,
      $orderby: 'DatestampInvoiced desc'
    });

    var ordersAfter = PurchaseOrders.query({
      $filter: 'DatestampInvoiced ge ' + statement.datestampEnd +
        ' and SupplierId eq ' + statement.supplierId +
        ' and DatestampDeleted eq null and Received eq true',
      $top: 2,
      $orderby: 'DatestampInvoiced asc'
    });

    $q.all([
      statementOrders.$promise,
      ordersBefore.$promise,
      ordersAfter.$promise
    ]).then(function() {
      promise.resolve({
        before: ordersBefore,
        after: ordersAfter,
        statement: statementOrders
      });
    }, function(response) {
      promise.reject(response);
    });

    return promise.promise;
  }

  /**
   * Get outstanding purchase orders
   *
   * @memberof StockPurchaseOrders
   */
  function getOutstanding() {
    return PurchaseOrders.query({
      $filter: 'Received eq false and DatestampDeleted eq null',
      $orderby: 'DatestampCreated'
    });
  }

  /**
   * Get all purchase orders based on given parameters
   *
   * @memberof StockPurchaseOrders
   * @param  {String} supplier    If set, limit the purchase orders to a supplier
   * @param  {String} startTime   If set, limit the purchase orders to after this date
   * @param  {String} endTime     If set, limit the purchase orders to before this date
   * @param  {String} search      If set, limit the purchase orders to those with an invoice no matching the search string
   * @param  {Object} tableParams Parameters from datatable directive to request a specific page.
   */
  function getHistory(supplier, startTime, endTime, search, tableParams) {
    var filter = [];
    if (supplier) {
      filter.push('SupplierId eq ' + supplier);
    }

    if (startTime) {
      filter.push('DatestampCreated gt ' + startTime);
    }

    if (endTime) {
      filter.push('DatestampCreated lt ' + endTime);
    }

    if (search) {
      var invoiceNoFilter = 'contains(InvoiceNo, \'' + search.replace(/\'/g, '\'\'') + '\')';
      //Because OrderNumber is of type int, querying with a non-int will result in an error.
      //if the search string isn't an int, only query the InvoiceNo field.
      if (!/^[0-9]*$/.test(search)) {
        filter.push(invoiceNoFilter);
      } else {
        var orderNumberPrefix = search.substring(0, 2);
        var orderNumber = search.substring(2);
        var orderNumberFilter = 'OrderNumber eq ' + search + ' or (OrderNumber eq ' + orderNumber + ' and OrderNumberPrefix eq \'' + orderNumberPrefix + '\')';
        filter.push('(' + invoiceNoFilter + ' or ' + orderNumberFilter + ')');
      }
    }

    var params = {
      $count: 'true',
      $top: tableParams.limit,
      $skip: tableParams.limit * (tableParams.page - 1),
      $orderby: tableParams.order + ' ' + ((tableParams.desc) ? 'desc' : 'asc')
    };

    if (filter.length > 0) {
      params.$filter = filter.join(' and ');
    }

    return PurchaseOrders.get(params);
  }

  /**
   * Get a blank adjusting purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {String} supplierId Id of the supplier the purchase order is for
   * @param  {String} date       Date of the purchase order
   */
  function getAdjusting(supplierId, date) {
    return PurchaseOrders.get({supplierId: supplierId, date: date});
  }

  /**
   * Get the communication log for a specific purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {String} orderId Id of the purchase order
   */
  function getCommLog(orderId) {
    return StockOrderCommunicationLogs.query({purchaseOrderId: orderId});
  }

  /**
   * Generate purchase orders based on a stock orders
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} stockOrder stock order model
   */
  function generate(stockOrder) {
    return StockOrders.save(stockOrder).$promise;
  }

  /**
   * Update a purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} purchaseOrder Updated purchase order model
   */
  function update(purchaseOrder) {
    if (purchaseOrder.id) {
      return PurchaseOrders.update(purchaseOrder).$promise;
    } else {
      return PurchaseOrders.save(purchaseOrder).$promise;
    }
  }

  function patch(orderId, field, newValue) {
    var delta = {};
    delta[field] = newValue;

    return PurchaseOrders.patch({id: orderId}, delta).$promise;
  }

  /**
   * Set a purchase order as deleted
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} order purchase order to delete
   */
  function remove(order) {
    return patch(order.id, 'datestampDeleted', moment().format());
  }

  /**
   * Set a purchase order as not deleted
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} order purchase order to update
   */
  function restore(order) {
    return patch(order.id, 'datestampDeleted', null);
  }

  /**
   * Download a purchase order as a PDF file
   *
   * @memberof StockPurchaseOrders
   * @param  {String} id Id of the purchase order to download
   */
  function print(id) {
    var result = $q.defer();

    PurchaseOrders.print({orderId: id}, function(response) {
      $window.location.assign(response.location);
      result.resolve();
    }, function(response) {
      result.reject(response);
    });

    return result.promise;
  }

  /**
   * Email a purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} sendModel email details
   */
  function email(sendModel) {
    return PurchaseOrders.email(sendModel).$promise;
  }

  /**
   * Fax a purchase order
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} sendModel fax details
   */
  function fax(sendModel) {
    return PurchaseOrders.fax(sendModel).$promise;
  }

  /**
   * Send a purchase order B2B
   *
   * @memberof StockPurchaseOrders
   * @param  {String} purchaseOrderId Id of the purchase order to send
   * @param  {Object} credentials     B2B credentials model
   */
  function direct(purchaseOrderId, credentials) {
    return PurchaseOrders.direct({id: purchaseOrderId}, credentials).$promise;
  }

  /**
   * Set a purchase order as received
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} order Purchase order to receive
   */
  function receive(order) {
    var receivedOrder = angular.copy(order);
    receivedOrder.received = true;
    return update(receivedOrder);
  }

  /**
   * Update the Xero Invoice Id for an order to be null
   *
   * @memberof StockPurchaseOrders
   * @param  {Object} order purchase order model
   */
  function unlinkFromXero(order) {
    return PurchaseOrders.patch({id: order.id}, {xeroInvoiceId: null}).$promise;
  }

  /**
   * Helper to determine the correct next purchase order poll delay period.
   *
   * @param {*} orders Orders returned from calling GET /StockPurchaseOrders
   * @param {*} immediate Is this a call that is forcing an immediate check?
   */
  function getPurchaseOrderPollDelay(orders, immediate) {
    var pollRequired = false;
    var lastEmailSendTime = new Date('1970-01-01T00:00:00Z');
    angular.forEach(orders, function(order) {
      // Only emails in these statuses are eligible for triggering polling
      if (order.status === 'Sending' || order.status === 'Sent') {
        pollRequired = true;
        var sendTime = new Date(order.sendTime);
        if (sendTime > lastEmailSendTime) {
          lastEmailSendTime = sendTime;
        }
      }
    });

    // We want to poll for POs that are in the Sending and Sent statuses, but
    // only up to the maximum delay time after the email sending was triggered.
    var msSinceLastEmailSent = helperUtils.now() - lastEmailSendTime.getTime();
    var currentPollDelaySlot = $filter('filter')(purchaseOrderPollDelaysInMs, function(delay) {
      return msSinceLastEmailSent < delay;
    });
    var currentPollDelay = currentPollDelaySlot.length ? currentPollDelaySlot[0] : NaN;
    var pollDelay = immediate ? 0 : currentPollDelay - msSinceLastEmailSent;

    // An NaN delay means that the email was sent more than our maximum
    // delay ago - we don't want to poll in this circumstance.
    if (isNaN(pollDelay)) {
      logger.info('no polling required due to all orders being too old');
      pollRequired = false;
    }

    return {
      required: pollRequired,
      delay: pollDelay
    };
  }

  return {
    get: get,
    getStockApi,
    getOutstanding: getOutstanding,
    getReceivedContaining: getReceivedContaining,
    getCommLog: getCommLog,
    getArchive: getArchive,
    getLineArchive: getLineArchive,
    getReceivedPurchaseOrdersByDateRange,
    getLineItemArchive: getLineItemArchive,
    getHistory: getHistory,
    getStatementOrders: getStatementOrders,
    getAdjusting: getAdjusting,
    generate: generate,
    update: update,
    remove: remove,
    restore: restore,
    print: print,
    email: email,
    fax: fax,
    direct: direct,
    receive: receive,
    unlinkFromXero: unlinkFromXero,
    getPurchaseOrderPollDelay: getPurchaseOrderPollDelay
  };
}]);
